import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styles from './HallOfHonor.module.css';

interface Season {
  id: number;
  name: string;
  seasonNumber: number;
  romanNumeral?: string;
  status: string;
}

interface GuildLeagueHistoryEntry {
  id: number;
  seasonId: number;
  guildId: number;
  guildName: string;
  finalPosition: number;
  totalWins: number;
  totalLosses: number;
  totalPoints: number;
  membersCount: number;
}

interface WallEntry {
  accountId: number;
  accountName: string | null;
  mercenaryName: string;
  title?: string | null;
  race: string;
  level: number;
  guildName: string | null;
  isDead?: boolean;
  isRetired?: boolean;
  value: number;
}

interface WallCategory {
  label: string;
  description: string;
  entries: WallEntry[];
}

interface WallOfLegendsData {
  seasonId: number;
  limit: number;
  categories: Record<string, WallCategory>;
}

interface Champion {
  id: number;
  accountId: number;
  seasonId: number;
  mercenaryName: string;
  guildName: string | null;
  details: Record<string, any> | null;
  Account?: { id: number; username: string; displayName: string };
}

const CATEGORY_ORDER = [
  'worldbreaker',
  'unending_storm',
  'untouchable',
  'bladedancer',
  'iron_wall',
  'crownbearer',
  'heartpiercer',
  'crown_of_bones',
  'punching_bag',
  'radiant_one',
  'the_dreaded',
];

const stripStatusSuffix = (name?: string | null) => (name || '').replace(/\s+\((Dead|Retired)\)$/, '');

const HallOfHonor: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'honors' | 'guilds'>('honors');
  const [wall, setWall] = useState<WallOfLegendsData | null>(null);
  const [champion, setChampion] = useState<Champion | null>(null);
  const [guildHistory, setGuildHistory] = useState<GuildLeagueHistoryEntry[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit] = useState<number>(10);

  const fetchSeasons = useCallback(async () => {
    try {
      const response = await axios.get('/api/seasons', { withCredentials: true });
      const completedSeasons = (response.data as Season[])
        .filter(s => s.status === 'completed')
        .sort((a, b) => b.seasonNumber - a.seasonNumber);
      setSeasons(completedSeasons);
      if (completedSeasons.length > 0) {
        setSelectedSeason(completedSeasons[0].id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching seasons:', err);
      setError('Failed to load seasons');
      setLoading(false);
    }
  }, []);

  const fetchHonors = useCallback(async () => {
    if (!selectedSeason) return;
    try {
      setLoading(true);
      const [wallRes, champRes] = await Promise.all([
        axios.get(`/api/seasons/${selectedSeason}/wall-of-legends`, {
          params: { limit },
          withCredentials: true,
        }),
        axios.get(`/api/seasons/${selectedSeason}/champion`, { withCredentials: true }),
      ]);
      setWall(wallRes.data);
      setChampion(champRes.data?.champion || null);
      setError(null);
    } catch (err) {
      console.error('Error fetching honors:', err);
      setError('Failed to load season honors');
    } finally {
      setLoading(false);
    }
  }, [selectedSeason, limit]);

  const fetchGuildHistory = useCallback(async () => {
    if (!selectedSeason) return;
    try {
      setLoading(true);
      const response = await axios.get('/api/seasons/history/guilds', {
        params: { seasonId: selectedSeason },
        withCredentials: true,
      });
      setGuildHistory(response.data.history || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching guild league history:', err);
      setError('Failed to load guild league history');
    } finally {
      setLoading(false);
    }
  }, [selectedSeason]);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  useEffect(() => {
    if (!selectedSeason) return;
    if (activeTab === 'honors') {
      fetchHonors();
    } else {
      fetchGuildHistory();
    }
  }, [selectedSeason, activeTab, fetchHonors, fetchGuildHistory]);

  const rankBadge = (rank: number): string => {
    const badges: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };
    return badges[rank] || `#${rank}`;
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Hall of Honor</h1>
      <p className={styles.subtitle}>Eternal glory to the champions of Njordheim</p>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'honors' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('honors')}
        >
          Wall of Legends
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'guilds' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('guilds')}
        >
          Guild League Standings
        </button>
      </div>

      <div className={styles.filters}>
        <label>Season:</label>
        <select
          value={selectedSeason || ''}
          onChange={(e) => setSelectedSeason(parseInt(e.target.value))}
          className={styles.select}
        >
          {seasons.length === 0 && <option value="">No completed seasons</option>}
          {seasons.map(season => (
            <option key={season.id} value={season.id}>
              {season.name}
            </option>
          ))}
        </select>
      </div>

      {loading && <div className={styles.loading}>Loading...</div>}
      {error && <div className={styles.error}>{error}</div>}

      {!loading && !selectedSeason && (
        <div className={styles.empty}>
          <p>No completed seasons yet.</p>
          <p>Champions will be immortalized here at the end of each season.</p>
        </div>
      )}

      {!loading && selectedSeason && activeTab === 'honors' && (
        <>
          {champion && (
            <div className={styles.championBanner}>
              <div className={styles.championCrown}>👑</div>
              <div>
                <div className={styles.championLabel}>Season Champion</div>
                <div className={styles.championName}>{champion.mercenaryName}</div>
                {champion.Account && (
                  <div className={styles.championUser}>
                    <a href={`/user/${champion.Account.id}`}>
                      {champion.Account.displayName || champion.Account.username}
                    </a>
                    {champion.guildName && <span> — {champion.guildName}</span>}
                  </div>
                )}
              </div>
            </div>
          )}

          {wall && Object.keys(wall.categories).length > 0 ? (
            <div className={styles.achievementsList}>
              {CATEGORY_ORDER.map(key => {
                const cat = wall.categories[key];
                if (!cat || cat.entries.length === 0) return null;
                return (
                  <div key={key} className={styles.achievementCategory}>
                    <div className={styles.categoryHeader}>
                      <div>
                        <h3 className={styles.categoryName} title={cat.description}>
                          {cat.label}
                        </h3>
                        <p className={styles.categoryDescription}>{cat.description}</p>
                      </div>
                    </div>
                    <div className={styles.rankList}>
                      {cat.entries.map((entry, idx) => (
                        <div key={`${entry.accountId}-${entry.mercenaryName}-${idx}`} className={styles.rankRow}>
                          <div className={styles.rankBadge}>{rankBadge(idx + 1)}</div>
                          <div className={styles.rankContent}>
                            <div className={`${styles.mercName}${entry.isDead ? ' ' + styles.deadMercName : ''}`}>
                              {stripStatusSuffix(entry.mercenaryName)}
                              {entry.isRetired ? ' (Retired)' : ''}
                            </div>
                            <div className={styles.mercMeta}>
                              {entry.title ? `${entry.title} — ` : ''}Level {entry.level} {entry.race}
                              {entry.guildName && <> — {entry.guildName}</>}
                              {entry.accountName && (
                                <>
                                  {' '}by{' '}
                                  <a href={`/user/${entry.accountId}`} className={styles.userLink}>
                                    {entry.accountName}
                                  </a>
                                </>
                              )}
                            </div>
                          </div>
                          <div className={styles.rankValue}>{entry.value.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.empty}>
              <p>No Wall of Legends data for this season.</p>
            </div>
          )}
        </>
      )}

      {!loading && selectedSeason && activeTab === 'guilds' && (
        <div className={styles.guildHistoryTable}>
          {guildHistory.length === 0 ? (
            <div className={styles.empty}>
              <p>No guild league data for this season.</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Guild</th>
                  <th>W-L</th>
                  <th>Points</th>
                  <th>Members</th>
                </tr>
              </thead>
              <tbody>
                {guildHistory.map((entry) => (
                  <tr key={entry.id}>
                    <td className={styles.position}>
                      {entry.finalPosition <= 3 ? rankBadge(entry.finalPosition) : `#${entry.finalPosition}`}
                    </td>
                    <td className={styles.guildName}>{entry.guildName}</td>
                    <td>{entry.totalWins}-{entry.totalLosses}</td>
                    <td>{entry.totalPoints}</td>
                    <td>{entry.membersCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default HallOfHonor;
